import { useEffect, useState } from 'react'
import { creatorProfileAPI } from '../services/api'
import { useThemeStore } from '../store/themeStore'

export type WhiteLabelStatus = 'checking' | 'platform' | 'storefront'

interface WhiteLabelBootstrapResult {
  status: WhiteLabelStatus
  creatorId: number | null
}

function isPlatformHostname(hostname: string): boolean {
  if (hostname === 'localhost' || hostname === '127.0.0.1') return true
  if (hostname.endsWith('.vercel.app')) return true

  const siteUrl = process.env.REACT_APP_SITE_URL
  if (siteUrl) {
    try {
      if (new URL(siteUrl).hostname === hostname) return true
    } catch {
      // malformed REACT_APP_SITE_URL - fall through to treating as custom domain
    }
  }
  return false
}

// Detects whether the app is being loaded from a creator's verified custom
// domain (white-label storefront) rather than the platform's own domain, and
// if so resolves the creator and applies their branding before the app shell
// renders.
const useWhiteLabelBootstrap = (): WhiteLabelBootstrapResult => {
  const [status, setStatus] = useState<WhiteLabelStatus>('checking')
  const [creatorId, setCreatorId] = useState<number | null>(null)
  const applyBranding = useThemeStore((state) => state.applyBranding)

  useEffect(() => {
    const hostname = window.location.hostname

    if (isPlatformHostname(hostname)) {
      setStatus('platform')
      return
    }

    let cancelled = false
    creatorProfileAPI
      .getProfileByDomain(hostname)
      .then((profile) => {
        if (cancelled) return
        applyBranding(profile.profile)
        setCreatorId(profile.creator.id)
        setStatus('storefront')
      })
      .catch(() => {
        if (!cancelled) setStatus('platform')
      })

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return { status, creatorId }
}

export default useWhiteLabelBootstrap
